import {observer} from 'mobx-react-lite'
import React, {useEffect, useState} from 'react'
import tw from 'tailwind-styled-components'
import {Lang} from 'yougile-lang'
import {DataObjectId, Hub, TaskColor, taskColors} from 'yougile-model'
import {Tracking, TrackingEvent} from '@common/tracking'
import {noPropagate, useForceUpdate, useSyncEvent} from '@containers/hooks'
import {TaskColorItem} from './task-color-item'

const TwBase = tw.div`
  py-8
  px-12
`

const TwHeader = tw.div`
  text-10
  leading-3
  text-secondary
  uppercase
`

const TwColorRow = tw.div`
  flex
  items-center
  justify-between
  mt-10
`

const delayToCheck = 7_000

interface Props {
  task: Hub
  className?: string
  closeMenu?: () => void
}

export const TaskColorPicker = observer((props: Props) => {
  if (!props.task) {
    return null
  }

  return <TaskColorPickerBase {...props} />
})

export const TaskColorPickerBase = observer((props: Props) => {
  const {task, className} = props
  const forceUpdate = useForceUpdate()
  useSyncEvent(
    'current',
    {
      filter: [task.id] as DataObjectId[],
      debounceTime: 100
    },
    () => {
      forceUpdate()
    },
    [task.id]
  )

  const taskColor = task.data?.color ?? 'task-primary'
  const [intermediateState, setIntermediateState] =
    useState<TaskColor>(taskColor)

  useEffect(() => {
    const tm = setTimeout(() => {
      if (taskColor !== intermediateState) {
        setIntermediateState(taskColor)
      }
    }, delayToCheck)

    return () => clearTimeout(tm)
  }, [intermediateState])

  const onChange = (color: TaskColor) => {
    setIntermediateState(color)

    task.data ??= {}
    task.data.color = color
    task.commit()

    void Tracking.send(TrackingEvent.taskChangeColor, {
      color: color
    })
  }

  return (
    <TwBase onClick={noPropagate} className={className}>
      <TwHeader>{Lang.msg.colorPicker.taskColumnColor}</TwHeader>
      <TwColorRow>
        {taskColors.map(color => {
          return (
            <TaskColorItem
              key={color}
              isSelected={color === taskColor}
              color={color}
              onClick={onChange}
            />
          )
        })}
      </TwColorRow>
    </TwBase>
  )
})
